import { Component, Output, EventEmitter, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SwButtonComponent } from '../sw-button/sw-button.component';
import { Topic } from '../../../core/models/topic.model';

const PRESET_COLORS = [
  '#00D2FF','#a78bfa','#f87171','#34d399','#fb923c',
  '#60a5fa','#f472b6','#facc15','#94a3b8','#e2e8f0',
];

@Component({
  selector: 'sw-topic-form',
  standalone: true,
  imports: [CommonModule, FormsModule, SwButtonComponent],
  template: `
    <div class="backdrop" (click)="cancel.emit()">
      <div class="panel glass" (click)="$event.stopPropagation()">

        <div class="panel-hdr">
          <div>
            <div class="mono" style="font-size:9px;color:var(--grey);letter-spacing:.12em;text-transform:uppercase;margin-bottom:6px;">// New Topic</div>
            <h2 class="section-title" style="font-size:20px;">Topic erstellen</h2>
          </div>
          <button class="close-btn" (click)="cancel.emit()">✕</button>
        </div>

        <div class="form-body">
          <div class="field">
            <div class="field-label">Name</div>
            <input [(ngModel)]="name" placeholder="z.B. Immogen" (input)="autoKey()">
          </div>

          <div class="field">
            <div class="field-label">Key (auto)</div>
            <input [(ngModel)]="key" placeholder="z.B. immogen" style="font-family:var(--font-mono);font-size:12px;">
          </div>

          <div class="field">
            <div class="field-label">Beschreibung</div>
            <textarea [(ngModel)]="description" placeholder="Worum geht es bei diesem Topic?" style="min-height:72px;"></textarea>
          </div>

          <div class="field">
            <div class="field-label">Topic-Hashtags</div>
            <input [(ngModel)]="hashtagsRaw"
              placeholder="#Immogen #Proptech #AIRealEstate"
              style="font-family:var(--font-mono);font-size:11px;">
            <div style="font-size:10px;color:var(--grey);margin-top:4px;">
              Mit Leerzeichen trennen. Werden bei jedem Post mit diesem Topic hinzugefügt.
            </div>
          </div>

          <div class="field">
            <div class="field-label">Farbe</div>
            <div class="color-row">
              @for (c of colors; track c) {
                <button class="color-swatch" [style.background]="c"
                  [class.selected]="selectedColor === c"
                  (click)="selectedColor = c"
                  [style.boxShadow]="selectedColor === c ? '0 0 0 2px #0B0E14, 0 0 0 3px ' + c : 'none'">
                </button>
              }
              <input type="color" class="color-custom" [(ngModel)]="selectedColor" title="Custom">
            </div>
            <div class="color-preview">
              <span class="color-dot" [style.background]="selectedColor" [style.boxShadow]="'0 0 10px ' + selectedColor + '66'"></span>
              <span style="font-family:var(--font-mono);font-size:9px;color:var(--grey);">{{ selectedColor }}</span>
            </div>
          </div>
        </div>

        <div class="panel-footer">
          <sw-button variant="ghost" (clicked)="cancel.emit()">Abbrechen</sw-button>
          <sw-button variant="primary" [disabled]="!name.trim()" (clicked)="submit()">Topic erstellen</sw-button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .backdrop {
      position: fixed; inset: 0; z-index: 100;
      background: rgba(0,0,0,0.6); backdrop-filter: blur(4px);
      display: flex; align-items: center; justify-content: center;
      animation: backdropIn 0.2s ease;
    }
    @keyframes backdropIn { from { opacity: 0; } to { opacity: 1; } }
    .panel {
      width: 420px; border-radius: 20px; overflow: hidden;
      animation: panelIn 0.25s cubic-bezier(0.16,1,0.3,1);
    }
    @keyframes panelIn {
      from { opacity: 0; transform: scale(0.95) translateY(10px); }
      to   { opacity: 1; transform: scale(1) translateY(0); }
    }
    .panel-hdr { display: flex; align-items: flex-start; justify-content: space-between; padding: 24px 24px 0; }
    .close-btn { background: rgba(255,255,255,0.05); border: 1px solid var(--border); color: var(--grey); width: 28px; height: 28px; border-radius: 8px; cursor: pointer; font-size: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.15s; }
    .close-btn:hover { background: rgba(255,255,255,0.1); color: var(--white); }
    .form-body { padding: 20px 24px; display: flex; flex-direction: column; gap: 16px; }
    .field { display: flex; flex-direction: column; gap: 6px; }
    .field-label { font-family: var(--font-mono); font-size: 9px; color: var(--grey); letter-spacing: 0.1em; text-transform: uppercase; }
    .color-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .color-swatch { width: 22px; height: 22px; border-radius: 50%; border: none; cursor: pointer; transition: transform 0.15s; flex-shrink: 0; }
    .color-swatch:hover { transform: scale(1.15); }
    .color-swatch.selected { transform: scale(1.1); }
    .color-custom { width: 22px; height: 22px; border-radius: 50%; border: 1px solid var(--border-s); padding: 0; cursor: pointer; background: none; }
    .color-preview { display: flex; align-items: center; gap: 8px; margin-top: 6px; }
    .color-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
    .panel-footer { display: flex; justify-content: flex-end; gap: 8px; padding: 16px 24px 24px; border-top: 1px solid var(--border); }
  `],
})
export class TopicFormComponent {
  @Output() created = new EventEmitter<Omit<Topic, 'active'>>();
  @Output() cancel  = new EventEmitter<void>();

  name          = '';
  key           = '';
  description   = '';
  hashtagsRaw   = '';
  selectedColor = '#00D2FF';
  readonly colors = PRESET_COLORS;

  autoKey() {
    this.key = this.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  }

  submit() {
    if (!this.name.trim()) return;
    const hashtags = this.hashtagsRaw.trim().split(/\s+/).filter(Boolean)
      .map(h => h.startsWith('#') ? h : '#' + h);
    this.created.emit({
      key: this.key || this.name.toLowerCase().replace(/\s+/g, '_'),
      name: this.name.trim(),
      description: this.description.trim(),
      color: this.selectedColor,
      hashtags,
    });
  }
}
